import { useState } from "react";
import { Box, Typography, Paper, Stack, TextField, Button, Alert, Chip, Grid2 as Grid, List, ListItem, ListItemText } from "@mui/material";
import { PageHeader } from "@/components/PageHeader";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { diagnosticsApi } from "@/api/diagnostics";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { formatDateTime } from "@/shared/datetime";

const COMMON = ["Stroke", "Brain hemorrhage", "Pneumothorax", "Pulmonary embolism", "Aortic dissection"];

export function DiagnosticsCriticalFindingsPage() {
  const canRaise = useAuth(s => s.hasPermission("diagnostics.finding.raise"));
  const canAck = useAuth(s => s.hasPermission("diagnostics.finding.acknowledge"));
  const qc = useQueryClient();
  const findings = useQuery({ queryKey: ["dx-rad-findings"], queryFn: () => diagnosticsApi.listCriticalFindings({ pendingOnly: true }) });
  const refresh = () => qc.invalidateQueries({ queryKey: ["dx-rad-findings"] });

  const [studyId, setStudyId] = useState(""); const [finding, setFinding] = useState(""); const [notes, setNotes] = useState("");
  const raise = useMutation({
    meta: { successMessage: "Critical finding raised — alert sent" },
    mutationFn: () => diagnosticsApi.raiseCriticalFinding({ studyId, finding: finding.trim(), notes: notes || null }),
    onSuccess: () => { setFinding(""); setNotes(""); refresh(); },
  });
  const ack = useMutation({ meta: { successMessage: "Acknowledged" }, mutationFn: (id: string) => diagnosticsApi.acknowledgeCriticalFinding(id, {}), onSuccess: refresh });

  return (
    <Box>
      <PageHeader breadcrumbs={["Diagnostics"]} title="Radiology critical findings" />
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 5 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Raise critical finding</Typography>
            <Stack spacing={1.5}>
              <TextField label="Study Id" size="small" value={studyId} onChange={e => setStudyId(e.target.value)} />
              <TextField label="Finding" size="small" value={finding} onChange={e => setFinding(e.target.value)} />
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {COMMON.map(f => <Chip key={f} size="small" variant="outlined" label={f} onClick={() => setFinding(f)} />)}
              </Stack>
              <TextField label="Notes" size="small" multiline minRows={2} value={notes} onChange={e => setNotes(e.target.value)} />
              {raise.isError && <Alert severity="error">{errorMessage(raise.error)}</Alert>}
              <Button variant="contained" color="error" disabled={!studyId || !finding.trim() || !canRaise || raise.isPending} onClick={() => raise.mutate()}>Raise & alert</Button>
            </Stack>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 7 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Pending findings</Typography>
            {findings.isError && <Alert severity="error" sx={{ mb: 1 }}>{errorMessage(findings.error)}</Alert>}
            <List dense>
              {(findings.data ?? []).map(f => (
                <ListItem key={f.id} secondaryAction={canAck ? <Button size="small" disabled={ack.isPending} onClick={() => ack.mutate(f.id)}>Acknowledge</Button> : null}>
                  <ListItemText primary={<><Chip size="small" color="error" label={f.finding} sx={{ mr: 1 }} />{f.notes}</>} secondary={`${f.raisedBy ?? "—"} · ${formatDateTime(f.raisedAt)}`} />
                </ListItem>
              ))}
              {(findings.data?.length ?? 0) === 0 && !findings.isLoading && <Typography variant="caption" color="text.secondary" sx={{ pl: 2 }}>No pending critical findings.</Typography>}
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
